




































































































import { Component, Prop, Watch, Vue } from "vue-property-decorator";
import { mixins } from "vue-class-component";

import VuePlotly from "@statnett/vue-plotly";

import buildUrl from "build-url";
import { endpoints } from "@/endpoints";
import { eventLoading } from "@/components/sce/EventLoading";
import { fmtPct, fmtDate } from "./formatters";
import PlotlyResizeMixin from "./plotly-resize-mixin";

interface PanelState {
    loading: boolean;
    error: string | null;
    data: any[];
}

function newPanelState(): PanelState {
    return { loading: false, error: null, data: [] };
}

@Component({ components: { VuePlotly } })
export default class DataCoverageTab extends mixins(PlotlyResizeMixin) {
    @Prop({ required: true }) startDate!: string;
    @Prop({ required: true }) endDate!: string;
    /** Full scenario-date list (from the dashboard root) — gives the heatmap its x-axis so even
     * fully-complete days show as a green column rather than vanishing. */
    @Prop({ default: () => [] }) availableDates!: string[];

    /** Per-state coverage summary (complete / partial / failed days). */
    states: PanelState = newPanelState();
    /** State-day completeness exceptions where a source feed failed. */
    completeness: PanelState = newPanelState();

    fmtPct = fmtPct;
    fmtDate = fmtDate;

    mounted() {
        this.fetchAll();
    }

    /** One key over both fetch inputs so a date-range change triggers exactly one fetch. Stacked
     * @Watch decorators fire once per changed prop, so changing the range (startDate + endDate in
     * the same tick) used to fetch every panel twice. */
    private get fetchParamsKey(): string {
        return `${this.startDate}|${this.endDate}`;
    }

    @Watch("fetchParamsKey")
    onParamsChanged() {
        this.fetchAll();
    }

    resizeCharts() {
        this.refitPlotly(["coverageChart"]);
    }

    fetchAll() {
        if (!this.startDate || !this.endDate) return;
        this.fetchPanel(
            this.states,
            endpoints.roadClosureAnalyticsStateCoverageUrl,
        );
        this.fetchPanel(
            this.completeness,
            endpoints.roadClosureAnalyticsCompletenessFlagsUrl,
        );
    }

    fetchPanel(panel: PanelState, url: string) {
        panel.loading = true;
        panel.error = null;
        eventLoading.startLoading();
        Vue.axios({
            url: buildUrl(url, {
                queryParams: {
                    start_date: this.startDate,
                    end_date: this.endDate,
                },
            }),
        })
            .then((response: any) => {
                panel.data = response.data || [];
            })
            .catch((err: any) => {
                console.error("[RCA Coverage]", err);
                panel.error = err?.message || "Failed to load.";
                panel.data = [];
            })
            .finally(() => {
                panel.loading = false;
                eventLoading.stopLoading();
            });
    }

    /**
     * Map the state-completeness code to a sensible chip colour. Codes seen so far in the data:
     *   - "partial" — at least one source feed was missing
     *   - "failed"  — every source feed for that state-day failed
     * Anything unexpected gets a neutral grey so a new code value renders without crashing.
     */
    completenessCodeColor(code: string | null | undefined): string {
        if (code === "partial") return "amber darken-2";
        if (code === "failed") return "red darken-2";
        return "blue-grey";
    }

    stateHeaders = [
        { text: "State / Territory", value: "state_code" },
        { text: "Selected days", value: "selected_day_count", align: "end" },
        { text: "Complete days", value: "complete_day_count", align: "end" },
        { text: "Partial days", value: "partial_day_count", align: "end" },
        { text: "Failed days", value: "failed_day_count", align: "end" },
        { text: "Complete rate", value: "complete_rate", align: "end" },
    ];

    completenessHeaders = [
        { text: "Date", value: "scenario_date_local" },
        { text: "State / Territory", value: "state_code" },
        { text: "Completeness", value: "state_completeness_code" },
        {
            text: "Failed sources",
            value: "failed_source_api_count",
            align: "end",
        },
        {
            text: "Expected sources",
            value: "expected_source_api_count",
            align: "end",
        },
        {
            text: "Failed source api list",
            value: "failed_source_api_list",
            align: "end",
        },
    ];

    get stateRows() {
        return this.states.data;
    }
    get completenessRows() {
        return this.completeness.data;
    }

    /**
     * Build a state/territory × scenario-date completeness grid: 0 complete, 1 partial, 2 failed.
     */
    get coveragePivot(): {
        z: number[][];
        text: string[][];
        x: string[];
        y: string[];
    } | null {
        const stateCodes = Array.from(
            new Set(
                (this.states.data || [])
                    .map((r: any) => String(r.state_code ?? ""))
                    .filter(Boolean),
            ),
        ).sort();
        const dates = (this.availableDates || [])
            .map((d) => String(d).slice(0, 10))
            .filter((d) => d >= this.startDate && d <= this.endDate)
            .sort();
        if (!stateCodes.length || !dates.length) return null;

        const exceptions = new Map<string, string>(); // `${state}__${date}` -> code
        for (const r of this.completeness.data || []) {
            const s = String(r.state_code ?? "");
            const d = String(r.scenario_date_local).slice(0, 10);
            exceptions.set(
                `${s}__${d}`,
                String(r.state_completeness_code ?? ""),
            );
        }
        const statusVal = (code: string | undefined) =>
            code === "failed" ? 2 : code === "partial" ? 1 : 0;
        const statusLabel = (v: number) =>
            v === 2 ? "Failed" : v === 1 ? "Partial" : "Complete";

        const z = stateCodes.map((s) =>
            dates.map((d) => statusVal(exceptions.get(`${s}__${d}`))),
        );
        const text = z.map((row) => row.map(statusLabel));
        return { z, text, x: dates, y: stateCodes };
    }

    get coverageTraces(): any[] {
        const p = this.coveragePivot;
        if (!p) return [];
        return [
            {
                type: "heatmap",
                z: p.z,
                x: p.x,
                y: p.y,
                text: p.text,
                colorscale: [
                    [0.0, "#dcfce7"],
                    [0.5, "#ff8f00"],
                    [1.0, "#b91c1c"],
                ],
                showscale: false,
                zmin: 0,
                zmax: 2,
                hovertemplate: "%{y} — %{x}<br>%{text}<extra></extra>",
            },
        ];
    }

    get coverageLayout(): any {
        const p = this.coveragePivot;
        const rowCount = p ? p.y.length : 1;
        const dates = p ? p.x : [];
        const targetLabels = 25;
        const step = Math.max(1, Math.ceil(dates.length / targetLabels));
        const tickvals = dates.filter((_d, i) => i % step === 0);
        const lastSample = dates[dates.length - 1] || "";
        const multiYear =
            (dates[0] || "").slice(0, 4) !== lastSample.slice(0, 4);
        const ticktext = tickvals.map((d) => {
            const dt = new Date(d + "T00:00:00");
            if (Number.isNaN(dt.getTime())) return d;
            return dt.toLocaleDateString("en-AU", {
                day: "2-digit",
                month: "short",
                year: multiYear ? "numeric" : undefined,
            });
        });
        return {
            dragmode: false,
            height: Math.max(220, 30 * rowCount + 90),
            margin: { l: 80, r: 60, t: 8, b: 100 },
            paper_bgcolor: "rgba(0,0,0,0)",
            plot_bgcolor: "rgba(0,0,0,0)",
            xaxis: {
                type: "category",
                automargin: true,
                tickangle: -45,
                tickmode: "array",
                tickvals,
                ticktext,
                title: { text: "Scenario date", standoff: 24 },
            },
            yaxis: {
                automargin: true,
                autorange: "reversed",
                ticksuffix: "  ",
            },
        };
    }
}
